import React, { useMemo, useState } from 'react';
import { useSet } from 'react-use';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../../../CommonComponents/OPL/Button/Button.styles';
import { Cell, HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { TableWrapper } from '../../../../../CommonComponents/OPL/Table/Table';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Wrapper } from '../../../../../CommonComponents/OPL/Wrapper/Wrapper';
import { PerformanceTableRow } from '../../../../components/PerformanceDetails/PerformanceDetails.styles';
import { TableData } from '../../../../hooks/useUnrealizedGainLoss';
import { TableCell } from '../TableCell/TableCell';
import {
  Sorting,
  SortingDirection,
} from '../../../Positions/components/AlternativeDetail/AlternativeDetail.meta';
import { useIsEmbeddedMobile } from '../../../../../_hooks/useIsEmbeddedMobile';
import { sortGainLossTableRows } from '../../../../helpers/sortTableRows';
import { useFilteredRows } from '../../hooks/useFilteredRows';
import { PositionsListMobile } from '../GainLossMobile/components/PoistionsListMobile';
import { PositionsDTO } from '../GainLossMobile/GainLossMobile.meta';
import { RealizedGainLossDTO } from '../../../../api/useRealizedGainLossQuery';
import { UnRealizedGainLossDTO } from '../../../../api/useUnrealizedGainLossQuery';

interface Props {
  data: {
    tableData: TableData;
    totals: {
      shortTerm: number;
      longTerm: number;
      total: number;
    };
    gainLoss: RealizedGainLossDTO[] | UnRealizedGainLossDTO[];
  };
  debouncedSearchTerm: string;
  setSearchTerm: (value: string) => void;
  searchTerm: string;
  title: string;
  setPositionMobile: (position: PositionsDTO) => void;
}

export const GainLossDetailsWrapper = ({
  data,
  debouncedSearchTerm,
  searchTerm,
  setSearchTerm,
  title,
  setPositionMobile,
}: Props) => {
  const [sorting, setSorting] = useState<Sorting>({});

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const [, { has: isRowExpanded, toggle: toggleRow }] = useSet<string>();
  const [, { has: isChildRowExpanded, toggle: toggleChildRow }] = useSet<string>();

  const unfilteredRows = useMemo(() => {
    if (!data?.tableData?.rows) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return data.tableData.rows;
    }

    const sortingKey = Object.keys(sorting)[0];

    return sortGainLossTableRows(data.tableData.rows, sortingKey, sorting[sortingKey]);
  }, [data, sorting]);

  const rows = useFilteredRows({ rows: unfilteredRows, filter: debouncedSearchTerm });

  const { tableData, gainLoss } = data;

  return (
    <Wrapper>
      <TableWrapper mb="4">
        <Box display="flex" flexDirection="column" p="12">
          {!isEmbeddedMobile && (
            <Text variant="cardTitle" mb="12">
              {title}
            </Text>
          )}
          <Input
            value={searchTerm}
            onChange={({ target: { value } }) => setSearchTerm(value)}
            placeholder="Search..."
            leftIcon="fa fa-search"
            aria-label="Search"
          />
        </Box>

        <HtmlTable
          variant="embedded"
          columnSpacing="medium"
          maxHeight={isEmbeddedMobile ? 'auto' : 600}
          fixedHead
        >
          <table>
            <thead>
              <tr>
                {tableData.headers.map(({ text, isMobile = false }, index) => (
                  <th
                    key={text}
                    style={{
                      display: !isEmbeddedMobile || isMobile ? 'table-cell' : 'none',
                    }}
                  >
                    <RawButton
                      onClick={() => {
                        if (sorting[index]) {
                          setSorting(
                            sorting[index] === SortingDirection.Asc
                              ? { [index]: SortingDirection.Desc }
                              : {},
                          );
                        } else {
                          setSorting({ [index]: SortingDirection.Asc });
                        }
                      }}
                      aria-label="Sort by column"
                      css="width: 100%"
                    >
                      <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                        <Text
                          variant="tableHeader"
                          textAlign={index === 0 ? 'left' : 'right'}
                          width="100%"
                        >
                          {text}
                        </Text>
                        {(sorting[index] === SortingDirection.Asc ||
                          sorting[index] === SortingDirection.Desc) && (
                          <Icon
                            className={
                              sorting[index] === SortingDirection.Asc
                                ? 'fas fa-arrow-up'
                                : 'fas fa-arrow-down'
                            }
                            color="neutral.500"
                          />
                        )}
                      </Box>
                    </RawButton>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {(rows ?? []).map(({ id, cells, childRows }, rowIndex) => {
                const hasChildRows = childRows && childRows.length > 0;
                const isExpanded = isRowExpanded(id) || (!!searchTerm && hasChildRows);
                const tableRowVariant = hasChildRows ? 'clickable' : 'default';

                return (
                  <React.Fragment key={id}>
                    <PerformanceTableRow
                      isExpanded={isExpanded}
                      onClick={hasChildRows && !searchTerm ? () => toggleRow(id) : undefined}
                      variant={tableRowVariant}
                    >
                      {cells.map(({ text, type, align, isMobile = false }, index) => (
                        <>
                          {(!isEmbeddedMobile || isMobile) && (
                            <Cell key={index} isFlex={index === 0}>
                              <Box display="flex" alignItems="center">
                                {index === 0 && hasChildRows && !searchTerm && (
                                  <Icon
                                    className={
                                      isExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'
                                    }
                                    color="neutral.400"
                                    mr={isExpanded ? 8 : 12}
                                  />
                                )}

                                {index === 0 ? (
                                  <>
                                    <TruncatedText
                                      fontWeight={600}
                                      color="neutral.700"
                                      textAlign="left"
                                      width="100%"
                                    >
                                      {text}
                                    </TruncatedText>
                                  </>
                                ) : (
                                  <TableCell text={text} type={type} align={align} />
                                )}
                              </Box>
                            </Cell>
                          )}
                        </>
                      ))}
                    </PerformanceTableRow>

                    {isExpanded && childRows && (
                      <>
                        {isEmbeddedMobile ? (
                          <PositionsListMobile
                            positions={gainLoss[rowIndex].positions}
                            setPositionMobile={setPositionMobile}
                          />
                        ) : (
                          childRows.map(({ id: childId, cells: childCells, lotDetailsRows }) => {
                            const hasLotDetails = lotDetailsRows && lotDetailsRows.length > 0;
                            const isChildExpanded =
                              isChildRowExpanded(id) || (!!searchTerm && hasLotDetails);
                            const childTableRowVariant = hasLotDetails ? 'clickable' : 'default';

                            return (
                              <React.Fragment key={childId}>
                                <PerformanceTableRow
                                  isExpanded={isChildExpanded}
                                  onClick={
                                    hasLotDetails && !searchTerm
                                      ? () => toggleChildRow(id)
                                      : undefined
                                  }
                                  variant={childTableRowVariant}
                                >
                                  {childCells.map((childCell, index) => (
                                    <Cell key={index} isFlex={index === 0}>
                                      <Box display="flex" alignItems="center">
                                        {index === 0 && hasChildRows && !searchTerm && (
                                          <Icon
                                            className={
                                              isChildExpanded
                                                ? 'fas fa-chevron-down'
                                                : 'fas fa-chevron-right'
                                            }
                                            color="neutral.400"
                                            mr={isChildExpanded ? 8 : 12}
                                            ml={8}
                                          />
                                        )}

                                        {index === 0 ? (
                                          <>
                                            <TruncatedText
                                              fontWeight={600}
                                              color="neutral.700"
                                              textAlign="left"
                                              width="100%"
                                            >
                                              {childCell.text}
                                            </TruncatedText>
                                          </>
                                        ) : (
                                          <TableCell
                                            text={childCell.text}
                                            type={childCell.type}
                                            align={childCell.align}
                                          />
                                        )}
                                      </Box>
                                    </Cell>
                                  ))}
                                </PerformanceTableRow>
                                {isChildExpanded &&
                                  lotDetailsRows &&
                                  lotDetailsRows.map(
                                    ({ id: lotDetailsId, cells: lotDetailsCells }) => {
                                      return (
                                        <React.Fragment key={lotDetailsId}>
                                          <PerformanceTableRow>
                                            {lotDetailsCells.map((lotDetailsCell, index) => (
                                              <Cell key={index} isFlex={index === 0}>
                                                <Box display="flex" alignItems="center">
                                                  <TableCell
                                                    text={lotDetailsCell.text}
                                                    type={lotDetailsCell.type}
                                                    align={lotDetailsCell.align}
                                                    fontWeight={400}
                                                  />
                                                </Box>
                                              </Cell>
                                            ))}
                                          </PerformanceTableRow>
                                        </React.Fragment>
                                      );
                                    },
                                  )}
                              </React.Fragment>
                            );
                          })
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </HtmlTable>
      </TableWrapper>
    </Wrapper>
  );
};
